'use client'

import { useState } from 'react'
import { startAuthentication } from '@simplewebauthn/browser'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ShieldAlert } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'

export type StepUpFactor = 'passkey' | 'password'

// ── S6: raw backend machine codes must never reach the user ────────────────
//
// `POST /ai-chat/step-up/verify` throws
// `new UnauthorizedException('step_up_verification_failed')` on a wrong
// password or a rejected passkey assertion. `ApiClientError.extractMessage`
// (lib/api/client.ts) returns `body.message` VERBATIM when that is the only
// field the body carries, so a user who typed the wrong password saw the
// literal string "step_up_verification_failed" rendered here. Known codes
// get a specific sentence; anything else that still LOOKS like a bare
// snake_case identifier (no spaces/punctuation — a WebAuthn DOMException
// message always has both, so it passes through untouched) gets a generic
// humanization rather than being shown raw.
const KNOWN_STEP_UP_ERRORS: Record<string, string> = {
  step_up_verification_failed: "That didn't verify. Please check and try again.",
}

function humanizeStepUpError(message: string): string {
  const known = KNOWN_STEP_UP_ERRORS[message]
  if (known) return known
  const looksLikeMachineCode = /^[a-z0-9]+(?:_[a-z0-9]+)+$/.test(message)
  if (!looksLikeMachineCode) return message
  const words = message.split('_').join(' ')
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`
}

export interface StepUpFactorPickerProps {
  localized?: boolean
  factors: StepUpFactor[]
  verifyReason: string
  passkeyOptions?: Record<string, unknown>
  onSubmit: (factor: StepUpFactor, payload: Record<string, unknown>) => Promise<void> | void
  onCancel: () => void
}

export function StepUpFactorPicker({
  factors,
  verifyReason,
  passkeyOptions,
  onSubmit,
  onCancel,
  localized = false,
}: StepUpFactorPickerProps) {
  const { t } = useTranslation()
  const copy = (key: string, original: string) => localized ? t(key) : original
  const [active, setActive] = useState<StepUpFactor>(factors[0])
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handlePasskey = async () => {
    setBusy(true)
    setError(null)
    try {
      if (!passkeyOptions) throw new Error('Passkey options are unavailable')
      const assertion = await startAuthentication({
        optionsJSON: passkeyOptions as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      })
      await onSubmit('passkey', assertion as unknown as Record<string, unknown>)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Passkey verification cancelled'
      setError(localized ? t('vishing.ai.verificationFailed') : humanizeStepUpError(msg))
    } finally {
      setBusy(false)
    }
  }

  const handlePassword = async () => {
    setBusy(true)
    setError(null)
    try {
      await onSubmit('password', { password })
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Wrong password'
      setError(localized ? t('vishing.ai.verificationFailed') : humanizeStepUpError(msg))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Alert>
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription>{verifyReason}</AlertDescription>
      </Alert>

      <Tabs value={active} onValueChange={(v) => setActive(v as StepUpFactor)}>
        <TabsList
          className="grid w-full"
          style={{ gridTemplateColumns: `repeat(${factors.length}, 1fr)` }}
        >
          {factors.includes('passkey') && (
            <TabsTrigger value="passkey">{copy('vishing.ai.passkey', 'Passkey')}</TabsTrigger>
          )}
          {factors.includes('password') && (
            <TabsTrigger value="password">{copy('vishing.ai.password', 'Password')}</TabsTrigger>
          )}
        </TabsList>

        {factors.includes('passkey') && (
          <TabsContent value="passkey" className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {copy('vishing.ai.registeredPasskey', 'Use your registered passkey (Touch ID, Windows Hello, security key).')}
            </p>
            <Button onClick={handlePasskey} disabled={busy} className="w-full">
              {busy ? copy('vishing.ai.verifying', 'Verifying…') : copy('vishing.ai.verifyPasskey', 'Verify with passkey')}
            </Button>
          </TabsContent>
        )}

        {factors.includes('password') && (
          <TabsContent value="password" className="space-y-3">
            <Label htmlFor="step-up-password">{copy('vishing.ai.accountPassword', 'Account password')}</Label>
            <Input
              id="step-up-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <Button
              onClick={handlePassword}
              disabled={busy || !password}
              className="w-full"
            >
              {busy ? copy('vishing.ai.verifying', 'Verifying…') : copy('vishing.ai.verify', 'Verify')}
            </Button>
          </TabsContent>
        )}

      </Tabs>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={busy}>
          {copy('vishing.library.cancel', 'Cancel')}
        </Button>
      </div>
    </div>
  )
}
